import {
	makeComputationApiRepository,
	makeMonitorRepository,
} from "../../../core/repositories/repositoryBuilders.js";
import AccountingDocumentService from "../../shared/documentInfrastructure/AccountingDocumentService.js";
import Budget from "../../../models/Budget.js";
import ComparisonReport from "../../../models/ComparisonReport.js";
import Forecast from "../../../models/forecast/instance/Forecast.js";
import ReportService from "../../reports2/ReportService.js";
import ReportTemplate from "../../../models/reports/templates/ReportTemplate.js";

/**
 * Service responsible for all data exchange required by the performance
 * comparison document. In particular, it is responsible for
 * interpreting the template and then fetching the report values.
 * This is delegated to an internal {@link ReportService} instance.
 */
export default class ComparisonToolDocumentService extends AccountingDocumentService {
	constructor({ period, company, chartOfAccountsId, session }) {
		super(...arguments);

		this._reportService = new ReportService({
			chartOfAccountsId,
			period,
			company,
		});

		this._incomeStatementRepository = makeMonitorRepository({
			route: "/Shared/ReportTemplate/Company/#companyId/income-statement",
			model: ReportTemplate,
			defaultFilters: {
				companyId: company.getId(),
			},
		});

		this._balanceSheetRepository = makeMonitorRepository({
			route: "/Shared/ReportTemplate/Company/#companyId/balance-sheet",
			model: ReportTemplate,
			defaultFilters: {
				companyId: company.getId(),
			},
		});

		this._forecastEstimateRepository = makeComputationApiRepository({
			route: "/Estimates",
			model: Forecast,
			session,
		});

		this._budgetRepository = makeMonitorRepository({
			route: "Accounting2/Company/#companyId/Budget/?id",
			model: Budget,
			defaultFilters: { companyId: company.getId() },
			antiForgeryToken: session.getAntiForgeryToken(),
		});
	}

	async fetchInitialData() {
		const [forecastEstimates, budgets, incomeStatement, balanceSheet] =
			await Promise.all([
				this._forecastEstimateRepository.get(),
				this._budgetRepository.get(),
				this._incomeStatementRepository.get(),
				this._balanceSheetRepository.get(),
				this._reportService.fetchChartOfAccountsAndKeyFigures(),
			]);

		return {
			chartOfAccounts: this._reportService.getChartOfAccounts(),
			forecastEstimates,
			budgets,
			incomeStatement,
			balanceSheet,
			period: this._reportService.getPeriod(),
		};
	}

	changePeriodOfReport({ period, reportTemplate }) {
		this.setPeriod(period);
		return this.updateReport({
			reportTemplate,
		});
	}

	async updateReport({ reportTemplate }) {
		this._reportService.setReportTemplate(reportTemplate);
		const report = await this._reportService.updateReportAndData();
		return new ComparisonReport({ report });
	}

	setPeriod(period) {
		this._reportService.setPeriod(period);
	}
}
